/**
 * OaSIS Explorer Frontend - Advanced Search Hook
 * Task 3.3: Advanced Search Page Implementation
 * Task 30.2: Usage Limits Frontend - Added usage info exposure
 * Task 30.7: Manual Search for non-Pro users - Auto-preview only for Pro
 * Task 32.6: Version-aware API calls
 * Task 33.5: Version-aware field mapping for renamed abilities
 * 
 * Custom hook for managing advanced search state and API integration
 */

import React, { useState, useCallback, useMemo } from 'react';
import { trpc } from '../api';
import { useLanguage, useAuth } from '../contexts';
import { useDebouncedSearch } from './useDebounce';
import { useAdvancedSearchUrlState } from './useUrlState';
import { useDatasetVersion } from './useDatasetVersion';
import { API_CONFIG, QUERY_OPTIONS, filterMeasurementsForVersion } from '../utils';
import { useSearchParams } from 'react-router-dom';
import { sortOccupations, parseSortParam, type SortableOccupation } from '../utils/sortingUtils';
import type { UsageInfo } from '../components/common/UsageIndicator';

// Search filter types
export interface SearchFilters {
  textSearch: string;
  hierarchyPrefixes: string[];
  teerLevels: number[]; // Array of selected TEER levels (0-5)
  measurements: Record<string, { min?: number; max?: number; exact?: number | string }>;
}

// Usage info from backend response
export type { UsageInfo };

// NO MORE HARDCODED MAPPINGS - Using real API endpoint

// Hierarchy categories
export const HIERARCHY_CATEGORIES = [
  { value: '0', label: 'Management occupations' },
  { value: '1', label: 'Business, finance and administration' },
  { value: '2', label: 'Natural and applied sciences' },
  { value: '3', label: 'Health occupations' },
  { value: '4', label: 'Education, law, community services' },
  { value: '5', label: 'Arts, culture, recreation and sport' },
  { value: '6', label: 'Sales and service occupations' },
  { value: '7', label: 'Trades, transport and equipment operators' },
  { value: '8', label: 'Natural resources, agriculture' },
  { value: '9', label: 'Manufacturing and utilities' },
] as const;

/**
 * Advanced search hook with dynamic field loading and API integration
 * Now includes URL state management for shareable/bookmarkable searches
 * Task 32.6: Version-aware API calls
 */
export const useAdvancedSearch = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { version, hasResolvedVersion } = useDatasetVersion(); // Task 32.6: Get version from URL
  const { getStateFromUrl, updateUrl } = useAdvancedSearchUrlState();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check subscription status to determine if user is Pro (Task 30.7)
  const subscriptionQuery = trpc.subscription.getStatus.useQuery(undefined, {
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  const isPro = subscriptionQuery.data?.isPro === true;
  
  // Track the last filters that were searched (for non-Pro users)
  // This prevents auto-fetch until user clicks Search with current filters
  const [lastSearchedFilters, setLastSearchedFilters] = useState<string | null>(null);
  
  // Load measurement fields dynamically from backend API - REAL IMPLEMENTATION
  // Task 32.6: Include version parameter
  const measurementFieldsQuery = trpc.occupation.getMeasurementFields.useQuery({
    language,
    version, // Task 32.6: Required version parameter
    includeMetadata: true,
  }, {
    ...QUERY_OPTIONS.CRITICAL,
    enabled: hasResolvedVersion,
  });
  
  // Search state using shared debounce hook
  const { searchQuery, setSearchQuery, debouncedQuery, clearSearch } = useDebouncedSearch();
  const [filters, setFilters] = React.useState<SearchFilters>({
    textSearch: '',
    hierarchyPrefixes: [],
    teerLevels: [],
    measurements: {},
  });

  // Flag to prevent URL update on initial mount
  const [isInitialized, setIsInitialized] = React.useState(false);
  
  // Infinite scroll state
  const [displayCount, setDisplayCount] = useState(20);
  
  // Read sort order from URL (default: 'title-asc')
  const { sortBy, sortOrder } = parseSortParam(searchParams.get('sort'));
  
  // Handle sort change
  const handleSortChange = useCallback((newSort: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', newSort);
    setSearchParams(newParams, { replace: true }); // Use replace to avoid adding to history
    setDisplayCount(20); // Reset display count when sort changes
  }, [searchParams, setSearchParams]);

  // Initialize state from URL on mount (URL takes priority)
  React.useEffect(() => {
    const urlState = getStateFromUrl();
    const sanitizedUrlState: SearchFilters = {
      ...urlState,
      measurements: filterMeasurementsForVersion(urlState.measurements, version),
    };
    
    // Only initialize if URL has any state
    const hasUrlFilters =
      sanitizedUrlState.textSearch ||
      sanitizedUrlState.hierarchyPrefixes.length > 0 ||
      sanitizedUrlState.teerLevels.length > 0 ||
      Object.keys(sanitizedUrlState.measurements).length > 0;
    if (hasUrlFilters) {
      setFilters(sanitizedUrlState);
      setSearchQuery(sanitizedUrlState.textSearch);
      // Don't auto-execute search from URL - users must click Search button
      // This prevents consuming credits on page refresh/load
      // Pro users will auto-execute via shouldAutoFetch (isPro === true)
    }
    
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Keep in-memory measurement filters valid for the active version.
  React.useEffect(() => {
    setFilters((prev) => {
      const versionFiltered = filterMeasurementsForVersion(prev.measurements, version);
      const prevKeys = Object.keys(prev.measurements);
      const filteredKeys = Object.keys(versionFiltered);
      const isSameShape =
        prevKeys.length === filteredKeys.length &&
        prevKeys.every((key) => Object.prototype.hasOwnProperty.call(versionFiltered, key));

      if (isSameShape) {
        return prev;
      }

      return {
        ...prev,
        measurements: versionFiltered,
      };
    });
  }, [version]);

  // Update text search filter when search query changes
  React.useEffect(() => {
    setFilters(prev => ({ ...prev, textSearch: debouncedQuery }));
  }, [debouncedQuery]);

  // Sync state to URL when filters change (debounced)
  React.useEffect(() => {
    if (!isInitialized) return; // Don't update URL during initialization
    
    updateUrl(filters, false); // Debounced update (now preserves sort param)
  }, [filters, updateUrl, isInitialized]);

  // Autocomplete query
  // Task 32.6: Include version parameter
  const autocompleteQuery = trpc.occupation.autocomplete.useQuery(
    {
      query: debouncedQuery,
      language,
      version, // Task 32.6: Required version parameter
      limit: API_CONFIG.SEARCH.AUTOCOMPLETE_LIMIT,
    },
    {
      enabled: hasResolvedVersion && debouncedQuery.length >= API_CONFIG.SEARCH.MIN_QUERY_LENGTH,
      ...QUERY_OPTIONS.REALTIME,
    }
  );

  // Check if filters are present
  const hasFiltersForSearch = Object.keys(filters.measurements).length > 0 || 
               filters.textSearch.length >= API_CONFIG.SEARCH.MIN_QUERY_LENGTH ||
               filters.hierarchyPrefixes.length > 0 ||
               filters.teerLevels.length > 0;

  // Current filters as a string key for comparison
  const currentFiltersKey = useMemo(() => JSON.stringify(filters), [filters]);
  
  // Task 30.7: Auto-preview for Pro users only, manual search for others
  // - Pro users: auto-fetch when filters change (unlimited searches)
  // - Free/Anonymous: only fetch when current filters match last searched filters
  const hasMatchingSearch = lastSearchedFilters === currentFiltersKey;
  const shouldAutoFetch = isPro || hasMatchingSearch;

  // Task 33.5: Map measurement filters to version-specific field names
  // Handles renamed fields (e.g., motorCoordination -> handEyeCoordination in 2025)
  const versionMappedMeasurements = useMemo(() => {
    return filterMeasurementsForVersion(filters.measurements, version);
  }, [filters.measurements, version]);

  // Main search results query - fetch ALL results for client-side sorting/pagination
  // Task 32.6: Include version parameter
  // Task 33.5: Use version-mapped measurements
  const searchResultsQuery = trpc.occupation.searchByMeasurements.useQuery(
    {
      language,
      version, // Task 32.6: Required version parameter
      textSearch: filters.textSearch,
      hierarchyPrefix: filters.hierarchyPrefixes.join(','), // Convert array to comma-separated string for API
      teerLevel: filters.teerLevels.length > 0 && filters.teerLevels.length < 6
        ? filters.teerLevels.join(',') 
        : undefined, // Convert array to comma-separated string for API
      limit: 'all', // Fetch all results for client-side sorting/pagination
      ...Object.entries(versionMappedMeasurements).reduce((acc, [key, value]) => {
        if (value.exact !== undefined) {
          // Handle hollandCodesAny as string, others as numbers
          if (key === 'hollandCodesAny') {
            acc[key] = String(value.exact);
          } else {
            acc[key] = value.exact;
          }
        } else {
          if (value.min !== undefined) acc[`${key}.lower`] = value.min;
          if (value.max !== undefined) acc[`${key}.upper`] = value.max;
        }
        return acc;
      }, {} as Record<string, number | string>),
    },
    {
      enabled: hasResolvedVersion && hasFiltersForSearch && shouldAutoFetch,
      staleTime: API_CONFIG.CACHE_DURATION.SHORT,
      // Don't retry on 429 (rate limit) - show limit modal immediately
      retry: (failureCount, error) => {
        const trpcError = error as { data?: { code?: string } };
        if (trpcError?.data?.code === 'TOO_MANY_REQUESTS') return false;
        return failureCount < 3; // Retry other errors up to 3 times
      },
    }
  );
  
  // Get tRPC utils for query invalidation
  const utils = trpc.useUtils();

  // Task 30.9: Invalidate usage query when search completes to update banner immediately
  React.useEffect(() => {
    if (searchResultsQuery.isSuccess && searchResultsQuery.data) {
      // Invalidate usage query so banner updates immediately
      utils.features.getUsage.invalidate({ featureId: 'ADVANCED_SEARCH' });
    }
  }, [searchResultsQuery.isSuccess, searchResultsQuery.data, utils]);

  // Manual search execution function (Task 30.7)
  const executeSearch = useCallback(() => {
    if (hasFiltersForSearch) {
      setLastSearchedFilters(currentFiltersKey);
      // The query will auto-refetch due to enabled becoming true
    }
  }, [hasFiltersForSearch, currentFiltersKey]);
  
  // Sort results client-side using shared utility
  const allSortedResults = useMemo(() => {
    type ResultType = SortableOccupation;
    const queryData = searchResultsQuery.data as {results?: ResultType[]} | undefined;
    const rawResults = queryData?.results || [];
    
    return sortOccupations(rawResults, sortBy, sortOrder);
  }, [searchResultsQuery.data, sortBy, sortOrder]);
  
  // Get visible results for infinite scroll
  const visibleResults = useMemo(() => {
    return allSortedResults.slice(0, displayCount);
  }, [allSortedResults, displayCount]);
  
  // Check if there are more results to load
  const hasMore = displayCount < allSortedResults.length;
  
  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    setDisplayCount(prev => prev + 20);
  }, []);
  
  // Reset display count when filters change
  React.useEffect(() => {
    setDisplayCount(20);
  }, [filters]);
  
  // Return augmented query with visible results
  const augmentedQuery = useMemo(() => {
    if (!searchResultsQuery.data) return searchResultsQuery;
    
    const queryData = searchResultsQuery.data as Record<string, unknown>;
    const metadata = (queryData.metadata as Record<string, unknown>) || {};
    
    return {
      ...searchResultsQuery,
      data: {
        ...queryData,
        results: visibleResults,
        metadata: {
          ...metadata,
          totalResults: allSortedResults.length,
          displayedCount: visibleResults.length,
    }
      }
    };
  }, [searchResultsQuery, visibleResults, allSortedResults.length]);

  // Extract usage info from search response (Task 30.2, Task 30.8B)
  const usageInfo: UsageInfo = useMemo(() => {
    const queryData = searchResultsQuery.data as { usage?: { remaining: number; limit: number | null; resetAt: string; tier: 'ANONYMOUS' | 'FREE' | 'PRO'; wasDuplicate?: boolean } } | undefined;
    const usage = queryData?.usage;
    
    return {
      remaining: usage?.remaining ?? null,
      limit: usage?.limit ?? null,
      resetAt: usage?.resetAt ?? null,
      tier: usage?.tier ?? null,
      wasDuplicate: usage?.wasDuplicate,  // Task 30.8B: Pass through duplicate flag
    };
  }, [searchResultsQuery.data]);

  // Check if limit was exceeded (429 error)
  const isLimitExceeded = useMemo(() => {
    const error = searchResultsQuery.error as { data?: { code?: string } } | null;
    return error?.data?.code === 'TOO_MANY_REQUESTS';
  }, [searchResultsQuery.error]);

  // Extract limit error info from 429 response
  const limitErrorInfo = useMemo(() => {
    if (!isLimitExceeded) return null;
    
    const error = searchResultsQuery.error as { 
      data?: { 
        cause?: { tier?: 'ANONYMOUS' | 'FREE'; resetAt?: string; limit?: number } 
      };
    } | null;
    
    const cause = error?.data?.cause;
    if (cause) return cause;
    
    // Fallback if cause extraction fails
    return { tier: 'FREE' as const, resetAt: null, limit: 4 };
  }, [isLimitExceeded, searchResultsQuery.error]);

  // Check if feature access was denied (403 error) - Task 30.6
  const isFeatureAccessDenied = useMemo(() => {
    const error = searchResultsQuery.error as { data?: { code?: string } } | null;
    return error?.data?.code === 'FORBIDDEN';
  }, [searchResultsQuery.error]);

  // Extract feature access error info from 403 response
  const featureAccessErrorInfo = useMemo(() => {
    if (!isFeatureAccessDenied) return null;
    
    const error = searchResultsQuery.error as { 
      data?: { 
        cause?: { featureId?: string; subFeatureId?: string; tier?: string } 
      };
      message?: string;
    } | null;
    
    const cause = error?.data?.cause;
    return {
      featureId: cause?.featureId ?? 'ADVANCED_SEARCH',
      subFeatureId: cause?.subFeatureId,
      tier: cause?.tier,
      message: error?.message ?? 'This feature is only available for Pro users',
    };
  }, [isFeatureAccessDenied, searchResultsQuery.error]);

  // Helper functions
  const clearAllFilters = () => {
    setFilters({
      textSearch: '',
      hierarchyPrefixes: [],
      teerLevels: [],
      measurements: {},
    });
    clearSearch();
  };

  const hasActiveFilters = hasFiltersForSearch;

  const updateMeasurementFilter = (field: string, type: 'min' | 'max' | 'exact', value: number | string | undefined) => {
    setFilters(prev => ({
      ...prev,
      measurements: {
        ...prev.measurements,
        [field]: {
          ...prev.measurements[field],
          [type]: value,
        },
      },
    }));
  };

  const removeMeasurementFilter = (field: string) => {
    setFilters(prev => ({
      ...prev,
      measurements: Object.fromEntries(
        Object.entries(prev.measurements).filter(([key]) => key !== field)
      ),
    }));
  };

  const updateHierarchyFilters = (prefixes: string[]) => {
    const newFilters = { ...filters, hierarchyPrefixes: prefixes };
    setFilters(newFilters);
    
    // Update URL immediately for hierarchy changes (not debounced)
    if (isInitialized) {
      updateUrl(newFilters, true); // immediate = true
    }
  };

  return {
    // State
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    hasActiveFilters,
    
    // API queries
    autocompleteQuery,
    searchResultsQuery: augmentedQuery,
    
    // Helper functions
    clearAllFilters,
    updateMeasurementFilter,
    removeMeasurementFilter,
    updateHierarchyFilters, // New: immediate URL update for hierarchy
    
    // Dynamic data from API - REAL BACKEND INTEGRATION
    measurementFieldsQuery,
    measurementCategories: ((measurementFieldsQuery.data as Record<string, unknown>)?.categories || {}) as Record<string, unknown>,
    
    // Constants
    HIERARCHY_CATEGORIES,
    
    // Sorting and infinite scroll
    sortBy,
    sortOrder,
    handleSortChange,
    hasMore,
    loadMore,
    
    // Usage tracking (Task 30.2)
    usageInfo,
    isLimitExceeded,
    limitErrorInfo,
    // Feature gating (Task 30.6)
    isFeatureAccessDenied,
    featureAccessErrorInfo,
    // Manual search mode (Task 30.7)
    isPro,
    executeSearch,
    needsManualSearch: !isPro && hasActiveFilters && !hasMatchingSearch,
  };
};

